import { LegalEntityCountry } from '@seapoint/server/enums';
import * as Sentry from '@sentry/react';
import { ComponentProps, useState } from 'react';
import { AccountView, BeneficiaryView } from '../../../../types/entity-types';
import { exhaustiveCheck } from '../../../../utils/exhaustive-check';
import { BeneficiaryListFlow } from '../../beneficiary-list-flow/beneficiary-list-flow';
import { CreateBeneficiaryFlow } from '../../create-beneficiary-flow/create-beneficiary-flow';
import { PaymentDetails } from '../../payment-flow-storage';
import { usePaymentFlow } from '../../use-payment-flow';
import { getBeneficiaryName } from '../../utils/get-beneficiary-name';
import { InvoiceDetailsWithOptionalCurrency } from './invoice-payment-information-without-toggle';
import { KnownBeneficiaryFlow } from './known-beneficiary-flow';
import { UnsupportedInvoiceDetails } from './unsupported-invoice-details';

type Steps = 'CREATE_BENEFICIARY' | 'ALL_BENEFICIARIES' | 'KNOWN_BENEFICIARY';

type KnownBeneficiaryNotPaidBeforeFlowProps = {
  companyCountry: LegalEntityCountry;
  accounts: AccountView[];
  beneficiaries: BeneficiaryView[];
  invoiceDetails: InvoiceDetailsWithOptionalCurrency;
  beneficiaryDetails: ComponentProps<typeof CreateBeneficiaryFlow>['initialValues'];
  isLoading: boolean;
  onCreateBeneficiary: (
    formValues: Parameters<ComponentProps<typeof CreateBeneficiaryFlow>['onCreateBeneficiary']>[0],
  ) => Promise<BeneficiaryView | undefined>;
  onMakePayment: (paymentDetails: PaymentDetails) => Promise<void>;
  onGoBack: () => void;
};

export const KnownBeneficiaryNotPaidBeforeFlow = ({
  companyCountry,
  accounts,
  beneficiaries,
  invoiceDetails,
  beneficiaryDetails,
  isLoading,
  onCreateBeneficiary,
  onMakePayment,
  onGoBack,
}: KnownBeneficiaryNotPaidBeforeFlowProps) => {
  const { getSelectedBeneficiaryId, updateSelectedBeneficiaryId, shouldRenderReviewStep } = usePaymentFlow();
  const [step, setStep] = useState<Steps>(shouldRenderReviewStep ? 'KNOWN_BENEFICIARY' : 'CREATE_BENEFICIARY');

  switch (step) {
    case 'CREATE_BENEFICIARY': {
      const beneficiaryName = getBeneficiaryName({
        legalName: beneficiaryDetails?.legalName,
        firstName: beneficiaryDetails?.firstName,
        lastName: beneficiaryDetails?.lastName,
      });

      return (
        <CreateBeneficiaryFlow
          companyCountry={companyCountry}
          initialValues={beneficiaryDetails}
          wrapperConfig={{
            visible: true,
            label: beneficiaryName,
            onSecondaryCtaClick: () => setStep('ALL_BENEFICIARIES'),
          }}
          isWarningCardVisible={true}
          isLoading={isLoading}
          onCreateBeneficiary={async (formValues) => {
            const beneficiary = await onCreateBeneficiary(formValues);

            if (!beneficiary) {
              return;
            }

            updateSelectedBeneficiaryId(beneficiary.id);

            setStep('KNOWN_BENEFICIARY');
          }}
          onGoBack={onGoBack}
        />
      );
    }
    case 'ALL_BENEFICIARIES': {
      const filteredBeneficiaries = invoiceDetails.currency
        ? // If we have an invoice currency, we should only display beneficiaries that match the currency of the invoice
          beneficiaries.filter((beneficiary) => beneficiary.destination.currency === invoiceDetails.currency)
        : // Otherwise, we display all beneficiaries because we don't know the currency of the invoice so the user will choose the currency
          beneficiaries;

      return (
        <BeneficiaryListFlow
          beneficiaries={filteredBeneficiaries}
          onGoBack={() => setStep('CREATE_BENEFICIARY')}
          onCreateBeneficiary={() => setStep('CREATE_BENEFICIARY')}
          onSelectBeneficiary={(beneficiaryId) => {
            updateSelectedBeneficiaryId(beneficiaryId);

            setStep('KNOWN_BENEFICIARY');
          }}
        />
      );
    }
    case 'KNOWN_BENEFICIARY': {
      const selectedBeneficiaryId = getSelectedBeneficiaryId();

      if (!selectedBeneficiaryId) {
        Sentry.captureMessage(
          'No beneficiary selected in KNOWN_BENEFICIARY step in DESTINATION_BENEFICIARY_NOT_PAID_BEFORE flow',
          {
            extra: {
              selectedBeneficiaryId,
              beneficiaryIds: beneficiaries.map((beneficiary) => beneficiary.id),
              step,
              message:
                'No beneficiary selected in KNOWN_BENEFICIARY step in DESTINATION_BENEFICIARY_NOT_PAID_BEFORE flow',
            },
          },
        );

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onGoBack}
          />
        );
      }

      const selectedBeneficiary = beneficiaries.find((beneficiary) => beneficiary.id === selectedBeneficiaryId);

      if (!selectedBeneficiary) {
        Sentry.captureMessage(
          'Selected beneficiary cannot be found among the list of beneficiaries in KNOWN_BENEFICIARY step in DESTINATION_BENEFICIARY_NOT_PAID_BEFORE flow',
          {
            extra: {
              selectedBeneficiaryId,
              beneficiaryIds: beneficiaries.map((beneficiary) => beneficiary.id),
              step,
              message:
                'Selected beneficiary cannot be found among the list of beneficiaries in KNOWN_BENEFICIARY step in DESTINATION_BENEFICIARY_NOT_PAID_BEFORE flow',
            },
          },
        );

        return (
          <UnsupportedInvoiceDetails
            title="Oups, something went wrong"
            variantConfig={{ variant: 'GENERIC_ERROR' }}
            onGoBack={onGoBack}
          />
        );
      }

      const beneficiaryCurrency = selectedBeneficiary.destination.currency;
      const beneficiariesWithMatchingCurrency = beneficiaries.filter(
        (beneficiary) => beneficiary.destination.currency === beneficiaryCurrency,
      );

      return (
        <KnownBeneficiaryFlow
          accounts={accounts}
          beneficiaries={beneficiariesWithMatchingCurrency}
          // We add the beneficiary that the user selected so the flow can work well
          initialInvoiceDetails={{
            ...invoiceDetails,
            currency: beneficiaryCurrency,
            suggestedBeneficiaryId: selectedBeneficiaryId,
          }}
          isLoading={isLoading}
          onMakePayment={onMakePayment}
          onCancelTransfer={() => setStep('ALL_BENEFICIARIES')}
        />
      );
    }
    default:
      return exhaustiveCheck(step);
  }
};
